/**
 * Google reCAPTCHA v3 Handler
 * Provides functions to generate and validate reCAPTCHA tokens
 */

class RecaptchaHandler {
  constructor() {
    this.config = window.recaptchaConfig || {};
    this.ready = false;
    this.readyPromise = null;
    this.init();
  }

  /**
   * Initialize reCAPTCHA when ready
   */
  init() {
    if (!this.config.siteKey || !this.config.validationEndpoint) {
      console.warn('reCAPTCHA configuration is incomplete');
      return;
    }

    if (typeof grecaptcha !== 'undefined') {
      grecaptcha.ready(() => {
        this.ready = true;
      });
    } else {
      setTimeout(() => this.init(), 100);
    }
  }

  /**
   * @returns {Promise<void>}
   */
  whenReady() {
    if (this.ready) {
      return Promise.resolve();
    }

    if (!this.readyPromise) {
      this.readyPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('reCAPTCHA failed to load'));
        }, 15000);

        const waitForReady = () => {
          if (this.ready) {
            clearTimeout(timeout);
            resolve();
            return;
          }

          if (typeof grecaptcha !== 'undefined') {
            grecaptcha.ready(() => {
              this.ready = true;
              clearTimeout(timeout);
              resolve();
            });
            return;
          }

          setTimeout(waitForReady, 100);
        };

        waitForReady();
      });
    }

    return this.readyPromise;
  }

  /**
   * Generate a reCAPTCHA token for a specific action
   * @param {string} action - The action name for this token
   * @returns {Promise<string>} The reCAPTCHA token
   */
  async generateToken(action = 'submit') {
    await this.whenReady();

    try {
      const token = await grecaptcha.execute(this.config.siteKey, { action });
      return token;
    } catch (error) {
      console.error('Error generating reCAPTCHA token:', error);
      throw error;
    }
  }

  /**
   * Validate a reCAPTCHA token with the backend
   * @param {string} token - The reCAPTCHA token to validate
   * @param {string} action - The action name for this token
   * @returns {Promise<object>} The validation response
   */
  async validateToken(token, action = 'submit') {
    if (!this.config.validationEndpoint) {
      throw new Error('Validation endpoint not configured');
    }

    try {
      const response = await fetch(this.config.validationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          action: action,
        }),
      });

      if (!response.ok) {
        throw new Error(`Validation failed with status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error validating reCAPTCHA token:', error);
      throw error;
    }
  }

  /**
   * Validate required form fields before reCAPTCHA (HTML5 + trim for text inputs).
   * @param {HTMLFormElement} form - The form element
   * @returns {boolean} True if all fields are valid
   */
  validateForm(form) {
    if (!form || !(form instanceof HTMLFormElement)) {
      return false;
    }

    const fields = form.querySelectorAll('input, textarea, select');

    fields.forEach((field) => {
      if (
          field.disabled ||
          field.type === 'hidden' ||
          field.type === 'submit' ||
          field.type === 'button'
      ) {
        return;
      }

      if (!field.hasAttribute('required')) {
        field.setCustomValidity('');
        return;
      }

      const isTextLike =
          field.type === 'text' ||
          field.type === 'email' ||
          field.type === 'tel' ||
          field.type === 'url' ||
          field.type === 'search' ||
          field.tagName === 'TEXTAREA';

      if (isTextLike && !field.value.trim()) {
        field.setCustomValidity('Please fill out this field.');
      } else {
        field.setCustomValidity('');
      }
    });

    return form.reportValidity();
  }

  /**
   * Inject token and post the form (form.submit() does not re-fire submit listeners).
   * @param {HTMLFormElement} form
   * @param {object} options
   * @returns {Promise<void>}
   */
  async processFormSubmission(form, options = {}) {
    const {
      action = 'submit',
      tokenFieldName = 'rcaptcha_token',
      callback = null,
    } = options;

    const token = await this.generateToken(action);
    const validation = await this.validateToken(token, action);

    if (!validation.success) {
      throw new Error(validation.message || 'reCAPTCHA validation failed');
    }

    let tokenInput = form.querySelector(`input[name="${tokenFieldName}"]`);
    if (!tokenInput) {
      tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = tokenFieldName;
      form.appendChild(tokenInput);
    }
    tokenInput.value = token;

    if (callback && typeof callback === 'function') {
      callback(validation);
    }

    form.submit();
  }

  /**
   * Bind synchronous submit interception (capture phase) and run reCAPTCHA async.
   * @param {HTMLFormElement} form
   * @param {object} options
   */
  bindForm(form, options = {}) {
    if (!form || !(form instanceof HTMLFormElement)) {
      console.error('Invalid form element provided');
      return;
    }

    const {
      action = 'submit',
      tokenFieldName = 'rcaptcha_token',
      callback = null,
      onError = null,
      errorMessage = 'An error occurred during security verification. Please try again.',
    } = options;

    if (form._recaptchaSubmitHandler) {
      form.removeEventListener('submit', form._recaptchaSubmitHandler, true);
    }

    const onSubmit = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (form.dataset.recaptchaProcessing === 'true') {
        return;
      }

      if (!this.validateForm(form)) {
        return;
      }

      form.dataset.recaptchaProcessing = 'true';

      const submitButton = form.querySelector('[type="submit"]');
      const originalButtonText = submitButton?.textContent;

      if (submitButton) {
        submitButton.disabled = true;
        if (form.dataset.recaptchaLoadingText) {
          submitButton.textContent = form.dataset.recaptchaLoadingText;
        }
      }

      let submitted = false;

      this.processFormSubmission(form, { action, tokenFieldName, callback })
          .then(() => {
            submitted = true;
          })
          .catch((error) => {
            console.error('reCAPTCHA error:', error);
            if (typeof onError === 'function') {
              onError(error, form);
            } else {
              alert(errorMessage);
            }
          })
          .finally(() => {
            form.dataset.recaptchaProcessing = '';
            if (!submitted && submitButton) {
              submitButton.disabled = false;
              if (originalButtonText) {
                submitButton.textContent = originalButtonText;
              }
            }
          });
    };

    form._recaptchaSubmitHandler = onSubmit;
    form.addEventListener('submit', onSubmit, true);
  }

  /**
   * Attach reCAPTCHA to a form
   * @param {HTMLFormElement} form - The form element
   * @param {string} action - The action name for this form
   * @param {function} callback - Optional callback function after validation
   */
  attachToForm(form, action = 'submit', callback = null) {
    this.bindForm(form, {
      action,
      callback,
      tokenFieldName: 'recaptcha_token',
    });
  }

  /**
   * Auto-bind Shopify contact forms
   */
  initContactForms() {
    if (!this.config.siteKey || !this.config.validationEndpoint) {
      return;
    }

    document.querySelectorAll('#ContactForm').forEach((form) => {
      if (form.dataset.recaptchaBound === 'true') {
        return;
      }

      form.dataset.recaptchaBound = 'true';
      this.bindForm(form, {
        action: 'contact_form',
        tokenFieldName: 'rcaptcha_token',
        errorMessage:
            'Security verification failed. Please try again.',
      });
    });
  }

  /**
   * Execute reCAPTCHA and validate in one step
   * @param {string} action - The action name
   * @returns {Promise<object>} The validation response
   */
  async executeAndValidate(action = 'submit') {
    const token = await this.generateToken(action);
    return await this.validateToken(token, action);
  }
}

window.recaptchaHandler = new RecaptchaHandler();

function initRecaptchaForms() {
  window.recaptchaHandler.initContactForms();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecaptchaForms);
} else {
  initRecaptchaForms();
}

document.addEventListener('shopify:section:load', initRecaptchaForms);

if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecaptchaHandler;
}
